import React, { useState, useEffect } from 'react';
import { formatearMoneda, limpiarMoneda } from '../utils/formatMoney';
import useResponsive from '../utils/useResponsive';
import { creditoExcedeInmueble, MENSAJE_CREDITO_INMUEBLE } from '../utils/creditoVsInmueble';
import ObligadoSolidarioBanregio, { erroresObligadoBanregio } from './ObligadoSolidarioBanregio';

const st = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #e11d48', paddingBottom: 8 },
  subtitle: { fontSize: 15, fontWeight: '600', color: '#374151', marginTop: 16, marginBottom: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 },
  moneyInput: { width: '100%', height: '42px', padding: '10px 12px 10px 24px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  saveBtn: { backgroundColor: '#1a3a6e', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 15, fontWeight: '600', cursor: 'pointer', marginTop: 8 },
  medRow: { display: 'flex', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #f0f0f0', gap: 16 },
  medLabel: { flex: 1, fontSize: 13, color: '#374151' },
  medSiNo: { display: 'flex', gap: 8, alignItems: 'center' },
  medExtra: { display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' },
};

const BANREGIO_ESTADO_CIVIL_MAP = {
  'Soltero': 'Soltero', 'Divorciado': 'Divorciado', 'Viudo': 'Viudo',
  // 'Casado' se resuelve con nombreEstadoCivilBanregio según régimen.
  // Criterio de Banregio: Union Libre y Sociedad de Convivencia se tratan como
  // Casado Sociedad Conyugal (equivalencia indicada por el usuario 2026-08-12).
  'Union Libre': 'Casado Sociedad Conyugal',
  'Sociedad de Convivencia': 'Casado Sociedad Conyugal',
};
function nombreEstadoCivilBanregio(estadoCivil, regimen) {
  if (estadoCivil === 'Casado') {
    if (regimen === 'Sociedad Conyugal') return 'Casado Sociedad Conyugal';
    if (regimen === 'Separación de Bienes') return 'Casado Separación de Bienes';
    return null;
  }
  return BANREGIO_ESTADO_CIVIL_MAP[estadoCivil] || null;
}
const BANREGIO_HABITA_CASA_MAP = {
  'Propia': 'Propia', 'Hipotecada': 'Hipotecada', 'Rentada': 'Rentada',
  'Padres': 'Vive con familiares', 'Otro Parentesco': 'Vive con familiares',
  // 'Otro' no tiene equivalente en el catálogo de Banregio — se deja sin mapear.
};
function idPorNombre(catalogo, nombre) {
  if (!nombre) return '';
  const match = catalogo.find(c => c.nombre === nombre);
  return match ? match.id : '';
}

const Sel = ({ label, value, onChange, options, placeholder = '— Selecciona —' }) => (
  <div style={st.field}>
    {label && <label style={st.label}>{label}</label>}
    <select value={value || ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} style={st.input}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
    </select>
  </div>
);

const Txt = ({ label, value, onChange, placeholder = '' }) => (
  <div style={st.field}>
    {label && <label style={st.label}>{label}</label>}
    <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={st.input} />
  </div>
);

const Money = ({ label, value, onChange, decimales = 2 }) => {
  const [enfocado, setEnfocado] = useState(false);
  return (
    <div style={st.field}>
      {label && <label style={st.label}>{label}</label>}
      <div style={st.moneyWrap}>
        <span style={st.moneySign}>$</span>
        <input type="text" inputMode="decimal"
          value={enfocado ? (value ?? '') : formatearMoneda(value, decimales)}
          onFocus={() => setEnfocado(true)}
          onBlur={() => setEnfocado(false)}
          onChange={e => onChange(limpiarMoneda(e.target.value, decimales))}
          style={st.moneyInput} placeholder={decimales === 0 ? '0' : '0.00'} />
      </div>
    </div>
  );
};

const SiNo = ({ value, onChange, defaultNo }) => {
  useEffect(() => {
    if (defaultNo && (value === undefined || value === null)) onChange(false);
  }, [value, defaultNo, onChange]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = (value === undefined || value === null) && defaultNo ? false : value;
  return (
    <div style={st.medSiNo}>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={Math.random()} checked={val === true} onChange={() => onChange(true)} /> Sí
      </label>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={Math.random()} checked={val === false} onChange={() => onChange(false)} /> No
      </label>
    </div>
  );
};

const MedPregunta = ({ num, label, campo, extra, br, set, defaultNo }) => (
  <div style={st.medRow}>
    <div style={st.medLabel}>
      <strong>{num}.</strong> {label}
      {extra && br[campo] && <div style={st.medExtra}>{extra}</div>}
    </div>
    <SiNo value={br[campo]} onChange={v => set(campo, v)} defaultNo={defaultNo} />
  </div>
);

const SeccionMedica = ({ prefix, titulo, br, set, frecuenciasCigarro, frecuenciasAlcohol }) => {
  const { isMobile } = useResponsive();
  return (
  <div style={st.card}>
    <div style={st.title}>{titulo}</div>

    <MedPregunta
      num="1" campo={`${prefix}_med_1_antecedentes`}
      label="¿Tiene o ha tenido algún antecedente familiar de enfermedades hereditarias o crónicas?"
      br={br} set={set} defaultNo
      extra={<>
        <Txt label="¿Quién?" value={br[`${prefix}_med_1_quien`]} onChange={v => set(`${prefix}_med_1_quien`, v)} />
        <Txt label="¿Cuándo?" value={br[`${prefix}_med_1_cuando`]} onChange={v => set(`${prefix}_med_1_cuando`, v)} />
      </>}
    />

    <div style={st.medRow}>
      <div style={st.medLabel}>
        <strong>2a.</strong> ¿Fuma?
        {br[`${prefix}_med_2a_fuma`] && (
          <div style={st.medExtra}>
            <Sel label="Frecuencia" value={br[`${prefix}_med_2a_frecuencia_id`]}
              onChange={v => set(`${prefix}_med_2a_frecuencia_id`, v)} options={frecuenciasCigarro} />
          </div>
        )}
      </div>
      <SiNo value={br[`${prefix}_med_2a_fuma`]} onChange={v => set(`${prefix}_med_2a_fuma`, v)} defaultNo />
    </div>

    <div style={st.medRow}>
      <div style={st.medLabel}>
        <strong>2b.</strong> ¿Consume bebidas alcohólicas?
        {br[`${prefix}_med_2b_alcohol`] && (
          <div style={st.medExtra}>
            <Sel label="Frecuencia" value={br[`${prefix}_med_2b_frecuencia_id`]}
              onChange={v => set(`${prefix}_med_2b_frecuencia_id`, v)} options={frecuenciasAlcohol} />
          </div>
        )}
      </div>
      <SiNo value={br[`${prefix}_med_2b_alcohol`]} onChange={v => set(`${prefix}_med_2b_alcohol`, v)} defaultNo />
    </div>

    <div style={st.medRow}>
      <div style={st.medLabel}>
        <strong>3.</strong> ¿Practica algún deporte?
        {br[`${prefix}_med_3_deporte`] && (
          <div style={st.medExtra}>
            <Txt label="¿Cuál?" value={br[`${prefix}_med_3_cual`]} onChange={v => set(`${prefix}_med_3_cual`, v)} />
            <Txt label="Frecuencia" value={br[`${prefix}_med_3_frecuencia`]} onChange={v => set(`${prefix}_med_3_frecuencia`, v)} />
          </div>
        )}
      </div>
      <SiNo value={br[`${prefix}_med_3_deporte`]} onChange={v => set(`${prefix}_med_3_deporte`, v)} />
    </div>

    <div style={st.medRow}>
      <div style={{ flex: 1 }}>
        <div style={st.medLabel}><strong>4.</strong> ¿Ha tenido variación de peso en el último año?</div>
        {br[`${prefix}_med_4_variacion_peso`] && (
          <div style={{ ...st.medExtra, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 12 }}>
            <div style={st.field}>
              <label style={st.label}>Aumentó (kg)</label>
              <input type="number" value={br[`${prefix}_med_4_aumento_kg`] || ''} onChange={e => set(`${prefix}_med_4_aumento_kg`, e.target.value)} style={st.input} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Peso actual (kg)</label>
              <input type="number" value={br[`${prefix}_med_4_peso_actual_kg`] || ''} onChange={e => set(`${prefix}_med_4_peso_actual_kg`, e.target.value)} style={st.input} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Disminuyó (kg)</label>
              <input type="number" value={br[`${prefix}_med_4_disminuyo_kg`] || ''} onChange={e => set(`${prefix}_med_4_disminuyo_kg`, e.target.value)} style={st.input} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Estatura (mts)</label>
              <input type="number" step="0.01" value={br[`${prefix}_med_4_estatura_mts`] || ''} onChange={e => set(`${prefix}_med_4_estatura_mts`, e.target.value)} style={st.input} />
            </div>
          </div>
        )}
      </div>
      <SiNo value={br[`${prefix}_med_4_variacion_peso`]} onChange={v => set(`${prefix}_med_4_variacion_peso`, v)} defaultNo />
    </div>

    <MedPregunta
      num="5" campo={`${prefix}_med_5_enfermedades`}
      label="¿Padece o ha padecido alguna enfermedad?"
      br={br} set={set} defaultNo
      extra={<Txt label="¿Cuál?" value={br[`${prefix}_med_5_cual`]} onChange={v => set(`${prefix}_med_5_cual`, v)} />}
    />

    <MedPregunta
      num="6" campo={`${prefix}_med_6_hospitalizado`}
      label="¿Ha sido hospitalizado en los últimos 5 años?"
      br={br} set={set} defaultNo
      extra={<Txt label="¿Motivo?" value={br[`${prefix}_med_6_cual`]} onChange={v => set(`${prefix}_med_6_cual`, v)} />}
    />

    <MedPregunta
      num="7" campo={`${prefix}_med_7_embarazo`}
      label="Si es mujer: ¿Ha tenido complicaciones en algún embarazo?"
      br={br} set={set} defaultNo
      extra={<Txt label="¿Cuál?" value={br[`${prefix}_med_7_cual`]} onChange={v => set(`${prefix}_med_7_cual`, v)} />}
    />
  </div>
  );
};

const BancoBanregio = ({ prospectoId, formData, setFormData }) => {
  const API = '/api';
  const { isMobile, isTablet } = useResponsive();
  const gridCols = (desktopCols) => (isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : desktopCols);
  const st2 = {
    grid2: { display: 'grid', gridTemplateColumns: gridCols('repeat(2, 1fr)'), gap: 16, marginBottom: 16 },
    grid3: { display: 'grid', gridTemplateColumns: gridCols('repeat(3, 1fr)'), gap: 16, marginBottom: 16 },
    grid4: { display: 'grid', gridTemplateColumns: gridCols('repeat(4, 1fr)'), gap: 16, marginBottom: 16 },
  };

  const [estadosCiviles,       setEstadosCiviles]       = useState([]);
  const [escolaridades,        setEscolaridades]        = useState([]);
  const [habitaCasas,          setHabitaCasas]          = useState([]);
  const [actividadesEco,       setActividadesEco]       = useState([]);
  const [giros,                setGiros]                = useState([]);
  const [tiposContrato,        setTiposContrato]        = useState([]);
  const [destinosCredito,      setDestinosCredito]      = useState([]);
  const [esquemas,             setEsquemas]             = useState([]);
  const [tiposInmueble,        setTiposInmueble]        = useState([]);
  const [plazos,               setPlazos]               = useState([]);
  const [frecuenciasCigarro,   setFrecuenciasCigarro]   = useState([]);
  const [frecuenciasAlcohol,   setFrecuenciasAlcohol]   = useState([]);

  const [br, setBr] = useState({});
  const [banregioCargado, setBanregioCargado] = useState(false);

  useEffect(() => { cargarCatalogos(); }, []);

  useEffect(() => {
    if (!prospectoId) { setBanregioCargado(true); return; }
    fetch(`${API}/banregio/${prospectoId}`)
      .then(r => r.ok ? r.json() : null)
      .then(datos => {
        if (datos && datos.id) {
          setBr(datos);
          setFormData(prev => ({ ...prev, banregio: datos }));
        }
      })
      .catch(e => console.error('Error cargando datos Banregio:', e))
      .finally(() => setBanregioCargado(true));
  }, [prospectoId]);

  // Estado Civil y Habita en Casa (Titular) se derivan del módulo Titular /
  // Coacreditado. Se recalculan cuando cambian ahí, y también una vez que
  // terminó de cargar lo guardado de Banregio, para no quedar pisados por un
  // valor anterior guardado en la base de datos.
  useEffect(() => {
    if (!banregioCargado) return;
    if (estadosCiviles.length === 0 || habitaCasas.length === 0) return;

    const ecId = idPorNombre(estadosCiviles, nombreEstadoCivilBanregio(formData.titular.estado_civil, formData.titular.regimen_matrimonio));
    const hcId = idPorNombre(habitaCasas, BANREGIO_HABITA_CASA_MAP[formData.titular.vive_en_casa] || null);

    setBr(prev => {
      const next = { ...prev };
      let changed = false;
      const set = (k, v) => { if (next[k] !== v) { next[k] = v; changed = true; } };
      set('titular_estado_civil_id', ecId);
      set('titular_habita_casa_id', hcId);
      return changed ? next : prev;
    });
  }, [
    banregioCargado, estadosCiviles, habitaCasas,
    formData.titular.estado_civil, formData.titular.regimen_matrimonio, formData.titular.vive_en_casa,
  ]);

  const cargarCatalogos = async () => {
    try {
      const cats = await Promise.all([
        fetch(`${API}/catalogos/cat_banregio_estado_civil`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_escolaridad`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_habita_casa`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_actividad_economica`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_giro`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_tipo_contrato`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_destino_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_esquema_adquisicion`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_tipo_inmueble`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_plazo_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_frecuencia_cigarro`).then(r => r.json()),
        fetch(`${API}/catalogos/cat_banregio_frecuencia_alcohol`).then(r => r.json()),
      ]);
      setEstadosCiviles(cats[0]);   setEscolaridades(cats[1]);
      setHabitaCasas(cats[2]);      setActividadesEco(cats[3]);
      setGiros(cats[4]);            setTiposContrato(cats[5]);
      setDestinosCredito(cats[6]);  setEsquemas(cats[7]);
      setTiposInmueble(cats[8]);    setPlazos(cats[9]);
      setFrecuenciasCigarro(cats[10]); setFrecuenciasAlcohol(cats[11]);
    } catch (e) { console.error('Error cargando catálogos Banregio:', e); }
  };

  const set = (campo, valor) => {
    const nuevo = { ...br, [campo]: valor };
    setBr(nuevo);
    setFormData(prev => ({ ...prev, banregio: nuevo }));
  };

  const guardar = async () => {
    if (creditoExcedeInmueble(br.importe_credito, br.valor_estimado_inmueble)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
    const errObligado = erroresObligadoBanregio(br.obligado_solidario);
    if (errObligado.length) { alert(errObligado.join('\n')); return; }
    try {
      const res = await fetch(`${API}/banregio/${prospectoId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(br),
      });
      if (res.ok) alert('Datos Banregio guardados correctamente');
      else {
        const j = await res.json().catch(() => ({}));
        alert(j.error ? `Error al guardar: ${j.error}` : 'Error al guardar');
      }
    } catch (e) { alert('Error de conexión'); }
  };

  const tieneCoac = !!formData.coacreditado?.nombres;
  const coacParticipaIngresos = formData.coacreditado?.participa_ingresos === true;

  return (
    <div>
      {/* ── Titular Información General ── */}
      <div style={st.card}>
        <div style={st.title}>Información General del Titular</div>
        <div style={st2.grid3}>
          <div style={st.field}>
            <label style={st.label}>Estado Civil</label>
            <input readOnly value={estadosCiviles.find(ec => ec.id === br.titular_estado_civil_id)?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <Sel label="Escolaridad" value={br.titular_escolaridad_id} onChange={v => set('titular_escolaridad_id', v)} options={escolaridades} />
          <div style={st.field}>
            <label style={st.label}>Habita en Casa</label>
            <input readOnly value={habitaCasas.find(hc => hc.id === br.titular_habita_casa_id)?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
        </div>
      </div>

      {/* ── Titular Información de Trabajo ── */}
      <div style={st.card}>
        <div style={st.title}>Información de Trabajo del Titular</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols('1fr 0.65fr 0.65fr 1.3fr'), gap: 16, marginBottom: 16 }}>
          <Sel label="Actividad Económica" value={br.titular_actividad_economica_id} onChange={v => set('titular_actividad_economica_id', v)} options={actividadesEco} />
          <Sel label="Giro" value={br.titular_giro_id} onChange={v => set('titular_giro_id', v)} options={giros} />
          <Sel label="Tipo de Contrato" value={br.titular_tipo_contrato_id} onChange={v => set('titular_tipo_contrato_id', v)} options={tiposContrato} />
          <Txt label="Puesto" value={br.titular_puesto} onChange={v => set('titular_puesto', v)} />
        </div>
        <div style={st2.grid3}>
          <Money label="Ingresos por Honorarios" decimales={0} value={br.titular_ingresos_honorarios} onChange={v => set('titular_ingresos_honorarios', v)} />
          <Money label="Ingresos por Sueldo" decimales={0} value={br.titular_ingresos_sueldo} onChange={v => set('titular_ingresos_sueldo', v)} />
          <Money label="Otros Ingresos" decimales={0} value={br.titular_otros_ingresos} onChange={v => set('titular_otros_ingresos', v)} />
        </div>
      </div>

      {/* ── Coacreditado Información General ── */}
      {tieneCoac && (
        <div style={st.card}>
          <div style={st.title}>Información General del Coacreditado</div>
          <div style={{ maxWidth: isMobile ? '100%' : 300 }}>
            <Sel label="Escolaridad" value={br.coac_escolaridad_id} onChange={v => set('coac_escolaridad_id', v)} options={escolaridades} />
          </div>
        </div>
      )}

      {/* ── Coacreditado Información de Trabajo ── */}
      {tieneCoac && coacParticipaIngresos && (
        <div style={st.card}>
          <div style={st.title}>Información de Trabajo del Coacreditado</div>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols('1fr 0.65fr 0.65fr 1.3fr'), gap: 16, marginBottom: 16 }}>
            <Sel label="Actividad Económica" value={br.coac_actividad_economica_id} onChange={v => set('coac_actividad_economica_id', v)} options={actividadesEco} />
            <Sel label="Giro" value={br.coac_giro_id} onChange={v => set('coac_giro_id', v)} options={giros} />
            <Sel label="Tipo de Contrato" value={br.coac_tipo_contrato_id} onChange={v => set('coac_tipo_contrato_id', v)} options={tiposContrato} />
            <Txt label="Puesto" value={br.coac_puesto} onChange={v => set('coac_puesto', v)} />
          </div>
          <div style={st2.grid3}>
            <Money label="Ingresos por Honorarios" decimales={0} value={br.coac_ingresos_honorarios} onChange={v => set('coac_ingresos_honorarios', v)} />
            <Money label="Ingresos por Sueldo" decimales={0} value={br.coac_ingresos_sueldo} onChange={v => set('coac_ingresos_sueldo', v)} />
            <Money label="Otros Ingresos" decimales={0} value={br.coac_otros_ingresos} onChange={v => set('coac_otros_ingresos', v)} />
          </div>
        </div>
      )}

      {/* ── Cuestionario Médico Titular ── */}
      <SeccionMedica prefix="titular" titulo="Cuestionario Médico del Titular" br={br} set={set} frecuenciasCigarro={frecuenciasCigarro} frecuenciasAlcohol={frecuenciasAlcohol} />

      {/* ── Cuestionario Médico Coacreditado ── */}
      {tieneCoac && (
        <SeccionMedica prefix="coac" titulo="Cuestionario Médico del Coacreditado" br={br} set={set} frecuenciasCigarro={frecuenciasCigarro} frecuenciasAlcohol={frecuenciasAlcohol} />
      )}

      {/* ── Obligado Solidario (mig 114): viaja en br.obligado_solidario dentro del mismo guardado ── */}
      <ObligadoSolidarioBanregio valor={br.obligado_solidario} onChange={v => set('obligado_solidario', v)}
        estadosCiviles={estadosCiviles} st={st} isMobile={isMobile} isTablet={isTablet} />

      {/* ── Información del Crédito ── */}
      <div style={st.card}>
        <div style={st.title}>Información del Crédito</div>

        <div style={st2.grid3}>
          <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
            <div style={st.field}>
              <label style={st.label}>Oferta Vinculante</label>
              <select value={br.oferta_vinculante === true ? 'true' : br.oferta_vinculante === false ? 'false' : ''}
                onChange={e => set('oferta_vinculante', e.target.value === '' ? null : e.target.value === 'true')} style={st.input}>
                <option value="">— Selecciona —</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>
          <div style={{ maxWidth: isMobile ? '100%' : '60%' }}>
            <Sel label="Destino de Crédito" value={br.destino_id} onChange={v => set('destino_id', v)} options={destinosCredito} />
          </div>
          <div style={{ maxWidth: isMobile ? '100%' : '60%' }}>
            <Money label="Importe del Crédito Solicitado" decimales={0} value={br.importe_credito} onChange={v => set('importe_credito', v)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridCols('0.5fr 0.5fr 0.5fr 0.5fr 2fr'), gap: 16, marginBottom: 16 }}>
          <Sel label="Plazo (años)" value={br.plazo_id} onChange={v => set('plazo_id', v)} options={plazos} />
          <div style={st.field}>
            <label style={st.label}>Tasa (%)</label>
            <input type="number" step="0.01" value={br.tasa || ''} onChange={e => set('tasa', e.target.value)} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Aforo (%)</label>
            <input type="number" value={br.aforo || ''} onChange={e => set('aforo', e.target.value)} style={st.input} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Inmueble</label>
            <select value={br.inmueble_nuevo === true ? 'true' : br.inmueble_nuevo === false ? 'false' : ''}
              onChange={e => set('inmueble_nuevo', e.target.value === '' ? null : e.target.value === 'true')} style={st.input}>
              <option value="">— Selecciona —</option>
              <option value="true">Nuevo</option>
              <option value="false">Usado</option>
            </select>
          </div>
        </div>

        <div style={st2.grid3}>
          <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
            <Sel label="Esquema de Adquisición" value={br.esquema_adquisicion_id} onChange={v => set('esquema_adquisicion_id', v)} options={esquemas} />
          </div>
          <div style={{ maxWidth: isMobile ? '100%' : '60%' }}>
            <Money label="Valor Estimado del Inmueble" decimales={0} value={br.valor_estimado_inmueble} onChange={v => set('valor_estimado_inmueble', v)} />
          </div>
        </div>
      </div>

      {/* ── Datos de la Garantía ── */}
      <div style={st.card}>
        <div style={st.title}>Datos de la Garantía</div>
        <div style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 8, marginBottom: 16 }}>
          <div style={{ width: isMobile ? '100%' : 215 }}>
            <Sel label="Tipo de Inmueble" value={br.tipo_inmueble_id} onChange={v => set('tipo_inmueble_id', v)} options={tiposInmueble} />
          </div>
          <div style={{ width: isMobile ? '100%' : 413 }}>
            <Txt label="Calle" value={br.garantia_calle} onChange={v => set('garantia_calle', v)} />
          </div>
          <div style={{ width: isMobile ? '100%' : 77 }}>
            <Txt label="No. Ext" value={br.garantia_no_ext} onChange={v => set('garantia_no_ext', v)} />
          </div>
          <div style={{ width: isMobile ? '100%' : 93 }}>
            <Txt label="No. Int" value={br.garantia_no_int} onChange={v => set('garantia_no_int', v)} />
          </div>
          <div style={{ width: isMobile ? '100%' : 344 }}>
            <Txt label="Colonia" value={br.garantia_colonia} onChange={v => set('garantia_colonia', v)} />
          </div>
        </div>
        <div style={st2.grid3}>
          <Txt label="C.P." value={br.garantia_cp} onChange={v => set('garantia_cp', v)} />
          <Txt label="Ciudad" value={br.garantia_ciudad} onChange={v => set('garantia_ciudad', v)} />
          <Txt label="Estado" value={br.garantia_estado} onChange={v => set('garantia_estado', v)} />
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <button style={st.saveBtn} onClick={guardar}>Guardar datos Banregio</button>
      </div>
    </div>
  );
};

export default BancoBanregio;
